import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getPublicBooking } from '../../services/maps';
import { getBooking, saveBooking } from '../../utils/session';

const SUPPORT_WA = '919999999999';

const POLL_MS = 30000;

const formatDateTime = (raw) => {
  if (!raw) return '';
  try {
    return new Date(raw).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch {
    return raw;
  }
};

const formatDate = (raw) => {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatTime = (raw) => {
  if (!raw) return '';
  if (/^\d{2}:\d{2}$/.test(raw)) {
    const [h, m] = raw.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hh = h % 12 || 12;
    return `${hh}:${String(m).padStart(2, '0')} ${ampm}`;
  }
  return raw;
};

const formatINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const STATUS_STYLES = {
  pending:   { bg: '#FEF3C7', border: '#F59E0B', color: '#92400E', label: 'Assigning driver…' },
  confirmed: { bg: '#DCFCE7', border: '#22C55E', color: '#166534', label: 'Driver assigned!' },
  cancelled: { bg: '#FEE2E2', border: '#EF4444', color: '#991B1B', label: 'Booking cancelled' },
  completed: { bg: '#E0E7FF', border: '#6366F1', color: '#3730A3', label: 'Trip completed' },
};

const BookingSuccess = () => {
  const { code } = useParams();
  const navigate = useNavigate();

  const session = getBooking();
  const initialBooking = session?.bookingCode === code ? session.booking : null;

  const [booking, setBooking] = useState(initialBooking);
  const [loading, setLoading] = useState(!initialBooking);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const fetchOnce = async () => {
      try {
        const result = await getPublicBooking(code);
        if (cancelled) return;
        setBooking(result.booking || null);
        setError(null);
        saveBooking({
          bookingCode: code,
          booking: result.booking || null,
          whatsapp: result.whatsapp || null,
        });
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setError('We could not find that booking. Please check the code and try again.');
        } else if (!booking) {
          setError('Could not load your booking. Please try again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchOnce();
    timerRef.current = setInterval(fetchOnce, POLL_MS);
    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const handleCopy = () => {
    navigator.clipboard?.writeText(code).then(
      () => toast.success('Booking code copied'),
      () => toast.error('Could not copy'),
    );
  };

  if (loading && !booking) {
    return <div style={containerStyle}><div style={cardStyle}>Loading your booking…</div></div>;
  }

  if (error && !booking) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h2 style={{ marginTop: 0 }}>Booking not found</h2>
          <p>{error}</p>
          <button style={primaryBtn} onClick={() => navigate('/')}>Back to home</button>
        </div>
      </div>
    );
  }

  const status = (booking?.status || 'pending').toLowerCase();
  const statusStyle = STATUS_STYLES[status] || STATUS_STYLES.pending;
  const driver = booking?.driver || null;
  const total = booking?.fare;
  const advance = booking?.advanceAmount;
  const balance = total && advance ? Number(total) - Number(advance) : null;
  const pickupAddr = booking?.pickupLocation || booking?.trip?.origin?.address;
  const dropAddr = booking?.dropoffLocation || booking?.trip?.destination?.address;
  const vehicleLabel = booking?.vehicleType || booking?.trip?.vehicle?.label;

  const supportUrl = `https://wa.me/${SUPPORT_WA}?text=${encodeURIComponent(`Hi ZilaCab, I need help with booking ${code}`)}`;

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
          <div style={checkmarkStyle}>✓</div>
          <h2 style={{ margin: '0.75rem 0 0.25rem', color: '#166534' }}>Booking Confirmed!</h2>
          <div style={{ color: '#666', fontSize: '0.95rem' }}>
            Save your booking code — you'll need it to track or cancel.
          </div>
        </div>

        <div style={codeBlockStyle}>
          <div style={{ fontSize: '0.85rem', color: '#777', textTransform: 'uppercase', letterSpacing: 1 }}>
            Booking Code
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
            <code style={codeStyle}>{code}</code>
            <button style={copyBtn} onClick={handleCopy}>Copy</button>
          </div>
        </div>

        <div style={{
          marginTop: '1rem',
          padding: '0.85rem 1rem',
          background: statusStyle.bg,
          border: `1px solid ${statusStyle.border}`,
          borderRadius: 10,
          color: statusStyle.color,
          fontSize: 14,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}>
          {status === 'pending' && <span style={pulseDotStyle} />}
          <span>{statusStyle.label}</span>
        </div>

        <div style={{
          marginTop: '0.75rem',
          padding: '0.85rem 1rem',
          background: '#F0FDF4',
          border: '1px solid #86EFAC',
          borderRadius: 10,
          color: '#166534',
          fontSize: 14,
          lineHeight: 1.5,
        }}>
          🔒 Payment received. Driver being assigned.
          <br />
          Full refund if no driver found in 24 hrs.
        </div>

        {booking && (
          <div style={{ marginTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.97rem' }}>
            <SectionTitle>Trip</SectionTitle>
            <Row label="From" value={pickupAddr || '—'} />
            <Row label="To" value={dropAddr || '—'} />
            <Row
              label="Departure"
              value={
                booking.pickupTime
                  ? formatDateTime(booking.pickupTime)
                  : `${formatDate(booking.travelDate)} ${formatTime(booking.pickupTime || '')}`.trim()
              }
            />
            {vehicleLabel && <Row label="Vehicle" value={String(vehicleLabel).toUpperCase()} />}
            {booking.distanceKm && <Row label="Distance" value={`${booking.distanceKm} km`} />}
            {total && <Row label="Total fare" value={formatINR(total)} />}
          </div>
        )}

        {status === 'confirmed' && driver && (
          <div style={{ marginTop: '1rem', padding: '0.85rem 1rem', background: '#F0FDF4', border: '1px solid #86EFAC', borderRadius: 10 }}>
            <SectionTitle>Driver</SectionTitle>
            <Row label="Name" value={driver.name || '—'} />
            {driver.vehicleModel && <Row label="Vehicle" value={`${driver.vehicleModel} · ${driver.vehiclePlate || ''}`} />}
            {driver.phone && (
              <a href={`tel:${driver.phone}`} style={{ ...primaryBtn, display: 'inline-block', textDecoration: 'none', marginTop: 8 }}>
                📞 Call driver
              </a>
            )}
          </div>
        )}

        {status === 'pending' && (
          <div style={{ marginTop: '1rem', padding: '0.85rem 1rem', background: '#EEF2FF', border: '1px solid #C7D2FE', borderRadius: 10, color: '#3730A3', fontSize: 14 }}>
            🚗 Our team is assigning a driver. You'll receive a WhatsApp update once confirmed.
          </div>
        )}

        {total && (
          <div style={{ marginTop: '1rem', padding: '0.85rem 1rem', background: '#FFF8E1', border: '1px solid #FFE0A3', borderRadius: 10, color: '#7A5A00', fontSize: 14 }}>
            <SectionTitle>Payment reminder</SectionTitle>
            {advance && <div>💵 Advance {formatINR(advance)} — pay driver at pickup</div>}
            {balance != null && <div>💵 Balance {formatINR(balance)} on arrival</div>}
            <div style={{ marginTop: 4, fontSize: 12, opacity: 0.8 }}>Cash only</div>
          </div>
        )}

        <div style={{ marginTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <a href={supportUrl} target="_blank" rel="noopener noreferrer" style={waBtnStyle}>
            📱 WhatsApp support
          </a>
          <Link to="/" style={{ ...secondaryBtn, textDecoration: 'none', textAlign: 'center' }}>🏠 Back to home</Link>
        </div>
      </div>
    </div>
  );
};

const SectionTitle = ({ children }) => (
  <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#666', marginBottom: 4 }}>
    {children}
  </div>
);

const Row = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem' }}>
    <span style={{ color: '#666' }}>{label}</span>
    <span style={{ fontWeight: 600, textAlign: 'right' }}>{value}</span>
  </div>
);

const containerStyle = { padding: '2rem 1rem', display: 'flex', justifyContent: 'center', background: '#FAF7F3', minHeight: 'calc(100vh - 80px)' };
const cardStyle = { background: '#fff', borderRadius: 16, padding: '1.5rem 1.25rem', maxWidth: 480, width: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' };
const checkmarkStyle = { width: 56, height: 56, borderRadius: '50%', background: '#22c55e', color: '#fff', fontSize: 30, lineHeight: '56px', display: 'inline-block', textAlign: 'center', fontWeight: 700 };
const codeBlockStyle = { background: '#f3f4f6', border: '1px dashed #c0c4cc', borderRadius: 10, padding: '0.85rem 1rem' };
const codeStyle = { fontSize: '1.5rem', fontWeight: 700, letterSpacing: 1.5, color: '#1f2937' };
const copyBtn = { padding: '0.4rem 0.85rem', borderRadius: 6, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontWeight: 600 };
const primaryBtn = { padding: '0.7rem 1rem', background: '#E8590A', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' };
const secondaryBtn = { padding: '0.7rem 1rem', background: '#fff', color: '#E8590A', border: '1px solid #E8590A', borderRadius: 8, fontWeight: 600, cursor: 'pointer' };
const waBtnStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
  padding: '0.75rem 1rem', background: '#25D366', color: '#fff', border: 'none',
  borderRadius: 8, fontWeight: 700, fontSize: '0.98rem', textDecoration: 'none', cursor: 'pointer',
};
const pulseDotStyle = {
  width: 10, height: 10, borderRadius: '50%',
  background: 'currentColor', display: 'inline-block', opacity: 0.7,
};

export default BookingSuccess;
